import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, Tab } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, useTab } from "@/comn/hooks";
import {
    APIS,
    SF_USR_APLY_INFO,
    SG_CO_TP_APLY_LIST,
    SF_CO_TP_DETAIL_INFO,
    FIELD_VISIBILITY_MAP,
    ATCH_FILE_VISIBILITY_MAP,
    CO_CD_INPUT_TRGT_SET,
    CO_CD_INPUT_PROPERTIES,
    SG_CO_EMP_LIST,
    SF_CO_EMP_INFO,
    SG_CO_HIST_LIST,
    SF_CO_HIST_INFO,
    SF_CO_DCLT_INFO,
    SG_CO_DCLT_LIST,
    URLS,
    PGEUIDS,
    CO_CD_LCNE_SET,
    SG_CO_EMP_CO_CD_LIST,
    CO_CD_INPUT_TASAC_SET,
} from "@/tra/tancis/ptli/usr/co/services/PtliCoService";
import { values } from "lodash";

/*
 * Company Information Management Edit
 * !== 업체 관리 수정 ==!
 */
export const PTLI0905003S = (): JSX.Element => {
    const { pgeUid } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast();
    const { coTin } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { pgeStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    const [coCdStatus, setCoCdStatus] = useState<string | null>();
    const [empStatus, setEmpStatus] = useState<string | null>(null);
    const [dcltStatus, setDcltStatus] = useState<string | null>(null);
    const [lcneNoIssCoTpList, setLcneNoIssCoTpList] = useState<string[]>([]);
    const lcmWrhsCoTpMapRef = useRef<any>({});
    const [currLcmWrhs, setCurrLcmWrhs] = useState<any>({});
    const [epzPrmss, setEpzPrmss] = useState<any>({});
    const [udtCoEmpRppnYn, setUdtCoEmpRppnYn] = useState("N");
    const [lcneUseTpCdList, setLcneUseTpCdList] = useState<any[]>([]);

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "LCM0006" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
            { area: "comnCd", comnCd: "PTL0001" },
            { area: "cstmCd" },
            { area: "cntyCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // User Information !== 사용자(대표자) 정보 ==!
        usrAplyInfo: useForm({ defaultSchema: SF_USR_APLY_INFO }),
        coTpAplyInfo: useForm({ defaultSchema: SF_CO_TP_DETAIL_INFO }),
        coEmpInfo: useForm({ defaultSchema: SF_CO_EMP_INFO }),
        coHistInfo: useForm({ defaultSchema: SF_CO_HIST_INFO }),
        coDcltInfo: useForm({ defaultSchema: SF_CO_DCLT_INFO }),
    };

    // Organization, Department Tab !== 기관,부서 탭 ==!
    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [{ label: "L_CO_INFO" }, { label: "L_CO_CD" }, { label: "L_CO_EMP" }, { label: "L_DCLT" }],
        },
    });

    const grid = {
        coTpList: useGrid({ defaultSchema: SG_CO_TP_APLY_LIST, page: pgeStore?.page, size: pgeStore?.size }),
        coEmpList: useGrid({ defaultSchema: SG_CO_EMP_LIST, page: pgeStore?.page, size: pgeStore?.size }),
        coHistList: useGrid({ defaultSchema: SG_CO_HIST_LIST, page: pgeStore?.page, size: pgeStore?.size }),
        coDcltList: useGrid({ defaultSchema: SG_CO_DCLT_LIST, page: pgeStore?.page, size: pgeStore?.size }),
        coEmpCoCdList: useGrid({ defaultSchema: SG_CO_EMP_CO_CD_LIST }),
    };

    const xtrnUsrIdntfNo = form.coEmpInfo.watch("xtrnUsrIdntfNo");
    const dcltTin = form.coDcltInfo.watch("dcltTin");

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 업체 사용자 정보 조회 ==!
        getCoInfoMgmt: useFetch({
            api: () => APIS.getCoInfoMgmt(coTin),
            onSuccess: (data) => {
                grid.coTpList.clearData();
                grid.coEmpList.clearData();
                grid.coHistList.clearData();

                setCoCdStatus(null);
                form.coTpAplyInfo.reset();
                form.coTpAplyInfo.clearValues({});

                setEmpStatus(null);
                form.coEmpInfo.reset();
                form.coEmpInfo.clearValues({});

                setDcltStatus(null);
                form.coDcltInfo.reset();
                form.coDcltInfo.clearValues({});

                grid.coDcltList.clearData();
                fetch.getCoDcltList.fetch();

                const coInfoMgmt = data.coInfoMgmt.content;
                form.usrAplyInfo.setValues(coInfoMgmt);
                grid.coTpList.setData({ content: coInfoMgmt.coTpList });
                grid.coEmpList.setData({ content: coInfoMgmt.coEmpList });
                grid.coHistList.setData({ content: coInfoMgmt.coHistList });

                lcmWrhsCoTpMapRef.current = coInfoMgmt.lcmWrhsCoTpMap;

                setCoCdStatus("select");
                fetch.getLcneIssCoTpList.fetch(coTin);
                fetch.getEpzLcnePrmssInfo.fetch(coTin);
            },
            onError: () => {},
            showToast: true,
        }),
        // Update Company And Representative Information !==  업체 및 대표자 정보 저장 ==!
        updateCoAndRppnInfo: useFetch({
            api: (data) => APIS.updateCoAndRppnInfo(data),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getCoInfoMgmt.fetch(coTin);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({ content: data.errorMessage });
            },
        }),
        // Create Company Code !== 업체 코드 등록 ==!
        createCoCd: useFetch({
            api: (data) => APIS.createCoCd({ ...data }),
            onSuccess: (x) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getCoInfoMgmt.fetch(coTin);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coTpAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({ content: data.errorMessage });
            },
        }),
        // Update Company Code !== 업체 코드 수정 ==!
        updateCoCd: useFetch({
            api: (data) => APIS.updateCoCd({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getCoInfoMgmt.fetch(coTin);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coTpAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({ content: data.errorMessage });
            },
        }),
        // delete Company Code !== 업체 코드 삭제 ==!
        deleteCoCd: useFetch({
            api: () => APIS.deleteCoCd(coTin, coSn),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00060", // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                    onCancel: () => {
                        fetch.getCoInfoMgmt.fetch(coTin);
                    },
                });
            },
            onError: (error) => {
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coTpAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
            showToast: true,
        }),
        getTinNoLstForUsr: useFetch({
            api: () => {
                return APIS.getPayerTinNoLst(xtrnUsrIdntfNo, "N");
            },
            onSuccess: (data) => {
                const tinNoList = data.tinNoList.content;

                if (tinNoList.length == 1) {
                    const tinNoInfo = tinNoList[0];

                    form.coEmpInfo.setValue("xtrnUsrIdntfNo", tinNoInfo.tinNo);
                    form.coEmpInfo.setValue("usrNm", tinNoInfo.coNm);
                    form.coEmpInfo.setValue("usrMoblNo", tinNoInfo.rprsTelno);
                    form.coEmpInfo.setValue("rsdtAddr", tinNoInfo.coAddr);
                    form.coEmpInfo.setValue("usrEmlAddr", tinNoInfo.rprsEml);

                    handler.checkUsrTin();
                } else {
                    toast.showToast({ type: "error", content: "msg.clr.00004" });
                }
            },
        }),
        // Create Company Employee User. !== 업체 직원 사용자를 생성 한다. ==!
        createCoEmpUsr: useFetch({
            api: (data) =>
                APIS.createCoEmpUsr({ ...data, empUsrCoTpList: grid.coEmpCoCdList.getData({ excludes: ["origin"] }) }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getCoInfoMgmt.fetch(coTin);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coEmpInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({ content: data.errorMessage });
            },
        }),
        // Modify Company Employee User. !== 업체 직원 사용자를 수정 ==!
        updateCoEmpUsr: useFetch({
            api: (data) =>
                APIS.updateCoEmpUsr({ ...data, empUsrCoTpList: grid.coEmpCoCdList.getData({ excludes: ["origin"] }) }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getCoInfoMgmt.fetch(coTin);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coEmpInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // delete Company Employee User !== 업체 직원 사용자 삭제 ==!
        deleteEmp: useFetch({
            api: (data) => APIS.deleteEmp({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00060", // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                    onCancel: () => {
                        fetch.getCoInfoMgmt.fetch(coTin);
                    },
                });
            },
            onError: (error) => {
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coEmpInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
            showToast: true,
        }),
        // Company Declarant Modify !== 업체 신고인 저장 ==!
        updateCoDclt: useFetch({
            api: (data) => APIS.updateCoDclt(data),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onConfirm: () => fetch.getCoInfoMgmt.fetch(),
                    close: false,
                });
            },
            onError: (error) => {
                modal.openModal({ content: error.response.data.errorMessage });
            },
        }),
        // Company Declarant Delete !== 업체 신고인 삭제 ==!
        deleteCoDclt: useFetch({
            api: (data) => APIS.deleteCoDclt(data),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onConfirm: () => fetch.getCoInfoMgmt.fetch(),
                    close: false,
                });
            },
            onError: (error) => {
                modal.openModal({
                    content: error.response.data.errorMessage,
                });
            },
        }),
        getCoDcltDtl: useFetch({
            api: () => APIS.getCoDcltDtl({ coTin, dcltTin }),
            onSuccess: (data) => {
                form.coDcltInfo.clearValues();

                const coDcltDtl = data.coDcltDtl.content;
                if (comnUtils.isEmpty(coDcltDtl)) {
                    // Unable to find the declarant TIN. !== 신고인 TIN을 찾을 수 없습니다. ==!
                    toast.showToast({ type: "error", content: "msg.clr.00087" });
                    return;
                }

                if (!comnUtils.isEmpty(coDcltDtl.dcltMgmtTin)) {
                    // This declarant has already been registered. !== 이미 등록된 신고인 입니다. ==!
                    toast.showToast({ type: "error", content: "msg.ptl.00757" });
                    return;
                }

                form.coDcltInfo.setValues(coDcltDtl);
            },
        }),
        createCoDclt: useFetch({
            api: (data) => APIS.saveCoDclt({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getCoInfoMgmt.fetch();
                    },
                    close: false,
                });
            },
            onError: (error) => {
                modal.openModal({ content: error.response.data.errorMessage });
            },
        }),
        getLcneIssCoTpList: useFetch({
            api: (coTin: string) => APIS.getLcneIssCoTpList(coTin),
            onSuccess: (data: any) => {
                const { content } = data.lcneIssCoTpList;

                let coTpList: string[] = [];
                const keyIterator = CO_CD_LCNE_SET.keys();
                while (true) {
                    const next = keyIterator.next();
                    if (next.done) break;

                    coTpList = [...coTpList, next.value];
                }

                if (!comnUtils.isEmpty(content)) {
                    setLcneNoIssCoTpList(coTpList.filter((value: string) => !content.includes(value)));
                } else {
                    setLcneNoIssCoTpList(coTpList);
                }
            },
        }),
        // User Tin duplicate check !== 사용자 TIN 중복 체크 ==!
        getCoDuplCheck: useFetch({
            api: (data: any) => APIS.getCoDuplCheck(data),
            enabled: false,
            onSuccess: (data) => {
                form.coEmpInfo.clearErrors("xtrnUsrIdntfNo");

                const coUsrInfo = data.coUsrInfo.content;

                if (!comnUtils.isEmpty(coUsrInfo)) {
                    if (coUsrInfo.coUsrYn === "Y") {
                        form.coEmpInfo.setError("xtrnUsrIdntfNo", {
                            message: "msg.com.00107", // Already registered user. !== 이미 등록된 사용자입니다. ==!
                            type: "error",
                        });

                        form.coEmpInfo.setValue("usrNm", null);
                        form.coEmpInfo.setValue("frgnrYn", null);
                        form.coEmpInfo.setValue("aplcCntyCd", null);
                        form.coEmpInfo.setValue("usrMoblNo", null);
                        form.coEmpInfo.setValue("rsdtAddr", null);
                        form.coEmpInfo.setValue("usrEmlAddr", null);

                        form.coEmpInfo.setValue("xtrnUsrRgsrYn", "N");
                    } else {
                        form.coEmpInfo.setValue("usrNm", coUsrInfo.usrNm);
                        form.coEmpInfo.setValue("frgnrYn", coUsrInfo.frgnrYn);
                        form.coEmpInfo.setValue("aplcCntyCd", coUsrInfo.aplcCntyCd);
                        form.coEmpInfo.setValue("usrMoblNo", coUsrInfo.usrMoblNo);
                        form.coEmpInfo.setValue("usrEmlAddr", coUsrInfo.usrEmlAddr);
                        form.coEmpInfo.setValue("rsdtAddr", coUsrInfo.rsdtAddr);

                        form.coEmpInfo.setValue("xtrnUsrRgsrYn", "Y");
                    }
                } else {
                    form.coEmpInfo.setValue("xtrnUsrRgsrYn", "N");
                }
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        // Reset the business user password. !== 업체 사용자 비밀번호를 초기화한다. ==!
        updateCoUsrPwdInit: useFetch({
            api: (data) => APIS.updateCoUsrPwdInit(data),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.ptl.00764", // A temporary password has been sent. !==  임시 비밀번호가 발송되었습니다. ==!
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                modal.openModal({ content: data.errorMessage });
            },
        }),

        getEmpUsrCoCdList: useFetch({
            api: (data: any) => APIS.getEmpUsrCocdList(data),
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        getCoDcltList: useFetch({
            api: () => APIS.getCoDcltList(coTin),
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        // Get EPZ License Permission Information !== EPZ 면허 허가 정보 ==!
        getEpzLcnePrmssInfo: useFetch({
            api: (coTin: string) => APIS.getEpzLcnePrmssInfo(coTin),
            onSuccess: (data) => {
                const epzLcnePrmssInfo = data.epzLcnePrmssInfo.content;
                setEpzPrmss(epzLcnePrmssInfo);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),

        chkWrhsCertificate: useFetch({
            api: (data: any) => APIS.chkWrhsCertificate(data),
            onSuccess: (data, param: any) => {
                const result = data.resultValue.content;
                data.resultValue.content.success = false;

                const todayStr = new Date().toISOString().slice(0, 10).replaceAll("-", "");

                if (result.tasacCrtfStatNm === "Active" && result.tasacCrtfVldEndDt > todayStr) {
                    if (param[0].coTpCd === "FF") {
                        if (result.tasacLcneTpCd !== "CCD") {
                            data.resultValue.content.errorMessage = "msg.ptl.00826";
                        }
                    } else if (param[0].coTpCd === "SA" || param[0].coTpCd === "SL") {
                        if (result.tasacLcneTpCd !== "SHP") {
                            data.resultValue.content.errorMessage = "msg.ptl.00827";
                        }
                    }
                } else {
                    data.resultValue.content.errorMessage = "msg.ptl.00825";
                }
                return;
            },
            onError: (err) => {
                modal.openModal({ content: err.response?.data?.errorMessage ?? "Unknown error" });
            },
            showToast: true,
        }),
    };

    const render = {
        coEmpList: {
            cell: {
                resetPwd: (props: any) => {
                    const { rowValues } = props;
                    return <Button onClick={() => handler.onPwdBtnClick(rowValues)}>{t("B_RESET_PWD")}</Button>;
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        getCoDcltDtl: () => {
            const dcltTin = form.coDcltInfo.getValue("dcltTin");
            if (comnUtils.isEmpty(dcltTin)) {
                toast.showToast({ type: "error", content: "msg.clr.00087" }); // Unable to find the declarant TIN. !== 신고인 TIN을 찾을 수 없습니다. ==!
                return;
            }

            fetch.getCoDcltDtl.fetch();
        },
        // Company Code List Cell Click  !== 업체 유형 목록 셀 클릭 ==!
        onCoTpListRowClick: (data: any) => {
            setCoCdStatus("update");
            form.coTpAplyInfo.setValues(data);
        },
        // Company Employee List Cell Click  !== 업체 직원 목록 셀 클릭 ==!
        onCoEmpListRowClick: (data: any) => {
            setEmpStatus("update");
            form.coEmpInfo.setValues({ ...data, mdfyRsnFile: data.mdfyRsnFile ?? undefined });
            setUdtCoEmpRppnYn(data.coRppnYn);

            grid.coEmpCoCdList.clearData();
            data.coRppnYn === "N" && fetch.getEmpUsrCoCdList.fetch(data);
            return;
        },
        // Company Declarant List Cell Click  !== 업체 신고인(CFA) 목록 셀 클릭 ==!
        onCoDcltListRowClick: (data: any) => {
            setDcltStatus("update");
            form.coDcltInfo.setValues(data);
        },
        // on Save Button Click Event Handler  !== 알림 등록 ==!
        onSaveBtnClick: () => {
            modal.openModal({
                content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.usrAplyInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        return;
                    }
                    form.usrAplyInfo.handleSubmit(
                        () => {
                            const usrAplyInfo = form.usrAplyInfo.getValues();
                            fetch.updateCoAndRppnInfo.fetch(usrAplyInfo);
                        },
                        () => {
                            toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        },
                    )();
                },
            });
        },
        // Company Code Add Button Click!== 업체코드 그리드 추가 버튼 ==!
        onCoCdAddBtnClick: () => {
            setCoCdStatus("new");
            form.coTpAplyInfo.clearValues({});
            form.coTpAplyInfo.setValue("coCd", "");
            form.coTpAplyInfo.setValue("coCdRo", "");
            form.coTpAplyInfo.setValue("coCdSelect", "");
            form.coTpAplyInfo.setValue("cstmOfceCd", "");
            form.coTpAplyInfo.setValue("coTpNm", form.usrAplyInfo.getValue("coNm"));
            //console.log(lcmWrhsCoTpMapRef.current);
            const excludes = [...form.coTpAplyInfo.schema.coTpCd.excludes, ...lcneNoIssCoTpList];
            form.coTpAplyInfo.setSchema("coTpCd", { excludes });
        },
        // on Save Company Code Button Click Event Handler  !== 업체 코드 등록 ==!
        onSaveCoCdBtnClick: () => {
            modal.openModal({
                content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: async () => {
                    if (!comnUtils.isEmpty(form.coTpAplyInfo.errors)) {
                        handler.showValidMsg();
                        return;
                    }

                    // Check for New Company Code Duplicate !== 신규 업체 유형 중복 확인 ==!
                    if (coCdStatus === "new") {
                        const coTpArray = grid.coTpList.getData();
                        // if (
                        //     coTpArray.some((item: any, index: any, self: any) => {
                        //         const newInfo = form.coTpAplyInfo.getValues();
                        //         return (
                        //             newInfo.coTpCd !== "C" &&
                        //             newInfo.coTpCd !== "CS" &&
                        //             //newInfo.coTpCd === "C" &&
                        //             //whsCoTpCds.indexOf(newInfo.coTpCd) === -1 &&
                        //             newInfo.cstmOfceCd === item.cstmOfceCd &&
                        //             newInfo.coTpCd === item.coTpCd
                        //         );
                        //     })
                        // ) {
                        //     toast.showToast({
                        //         type: "warning",
                        //         content: t("msg.cgm.00383", { 0: t("L_CO_TP") + ", " + t("L_CSTM_OFCE_CD") }),
                        //     }); // Duplicate data exists. {0: Company Type, Customs Office Code} !== 중복된 데이터가 존재합니다. {0: 업체 타입, 세관 사무소 코드} ==!
                        //     return;
                        // }
                        if (
                            coTpArray.some((item: any) => {
                                const newInfo = form.coTpAplyInfo.getValues();

                                // Only check duplicates for CFA TIN
                                if (newInfo.coTpCd === "C") {
                                    return (
                                        newInfo.coTpCd === item.coTpCd &&
                                        newInfo.cstmOfceCd === item.cstmOfceCd &&
                                        newInfo.tinNo === item.tinNo
                                    );
                                }

                                return false; // allow duplicates for all other types
                            })
                        ) {
                            toast.showToast({
                                type: "warning",
                                content: t("msg.cgm.00383", { 0: t("L_CO_TP") + ", " + t("L_CSTM_OFCE_CD") }),
                            });
                            return;
                        }

                        const newInfo = form.coTpAplyInfo.getValues();
                        let hasError = false;
                        let errorMessage;

                        if (CO_CD_INPUT_TASAC_SET.has(newInfo.coTpCd)) {
                            newInfo.tasacRgsrNo = form.usrAplyInfo.getValue("coTin");
                            const res = await fetch.chkWrhsCertificate.fetch(newInfo);
                            // errorMessage = res.message;
                            if (res.resultValue !== undefined) {
                                errorMessage = res.resultValue.content.errorMessage;
                                if (errorMessage !== undefined) {
                                    hasError = true;
                                }
                            } else {
                                return;
                            }
                        }

                        if (hasError) {
                            modal.openModal({
                                content: errorMessage,
                            });
                            return;
                        }
                    }
                    // return false;
                    handler.onSaveCoCd();
                },
            });
        },

        onSaveCoCd: () => {
            form.coTpAplyInfo.handleSubmit(
                () => {
                    const data = form.coTpAplyInfo.getValues();

                    if (typeof data.bsopId === "object") {
                        data.atchFile = data.bsopId;
                        data.bsopId = null;
                    }

                    if (coCdStatus === "new") {
                        if (CO_CD_LCNE_SET.has(data.coTpCd) && data.hasOwnProperty("coCdSelect")) {
                            data.coCd = data.coCdSelect;
                        }

                        fetch.createCoCd.fetch({ ...data, coTin: coTin });
                    } else if (coCdStatus === "update") {
                        fetch.updateCoCd.fetch({ ...data });
                    }
                },
                () => {
                    handler.showValidMsg();
                    //toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        // Delete Company Code !== 업체 코드 삭제 ==!
        onDeleteCoCdBtnClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    fetch.deleteCoCd.fetch();
                },
            });
        },
        // Employee Add Button Click!== 업체코드 그리드 추가 버튼 ==!
        onEmpAddBtnClick: () => {
            //2025.07.18
            const hasCS = !lcneNoIssCoTpList.includes("CS"); // -1이 아니면 cs 라이센스가 없는것. 따라서 -1이면 CS 라이센스가 있는것
            const coEmpList = grid.coEmpList.getData();
            let realEmployeeCount = 0;

            for (let i = 0; i < coEmpList.length; i++) {
                if (coEmpList[i].delYn === "N") {
                    realEmployeeCount++;
                }
            }

            if (hasCS && realEmployeeCount >= 2) {
                // 경고, 오류 메시지 또는 처리 로직
                // toast.showToast({
                //     type: "warning",
                //     content: "msg.ptl.00829",
                // });
                // return;
                modal.openModal({ content: "msg.ptl.00829" }); // Please select a registration target !== 등록대상을 선택하세요. ==!
                return;
            }
            setEmpStatus("new");
            form.coEmpInfo.clearValues({ usrStatCd: "0" });
            form.coEmpInfo.setValues({ usrStatCd: "0", frgnrYn: "N" });

            fetch.getEmpUsrCoCdList.fetch({ coTin });
        },
        // on Save Company Code Button Click Event Handler  !== 업체 코드 등록 ==!
        onSaveEmpBtnClick: () => {
            modal.openModal({
                content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.coEmpInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        return;
                    }

                    const hasCS = !lcneNoIssCoTpList.includes("CS"); // -1이 아니면 cs 라이센스가 없는것. 따라서 -1이면 CS 라이센스가 있는것
                    const coEmpList = grid.coEmpList.getData();
                    let realEmployeeCount = 0;

                    for (let i = 0; i < coEmpList.length; i++) {
                        if (coEmpList[i].delYn === "N") {
                            realEmployeeCount++;
                        }
                    }
                    if (hasCS && realEmployeeCount > 2) {
                        // 경고, 오류 메시지 또는 처리 로직
                        // toast.showToast({
                        //     type: "warning",
                        //     content: "msg.ptl.00829",
                        // });
                        modal.openModal({ content: "msg.ptl.00829" }); // Please select a registration target !== 등록대상을 선택하세요. ==!
                        return;
                    }

                    const data = form.coEmpInfo.getValues();
                    const coRppnXtrnUsrUuid = form.usrAplyInfo.getValue("coRppnXtrnUsrUuid");
                    data.coRppnXtrnUsrUuid = coRppnXtrnUsrUuid;

                    if (empStatus === "update" && udtCoEmpRppnYn === "Y" && data.coRppnYn === "N") {
                        toast.showToast({ type: "warning", content: "msg.ptl.00821" }); // If you want to cancel the representative, set up another employee as the representative and apply it. !== 대표자를 해지 하시려면 다른 직원을 대표자로 설정하여 적용하세요. ==!
                        return;
                    }

                    if (data.coRppnYn == "Y" && coRppnXtrnUsrUuid && coRppnXtrnUsrUuid != data.xtrnUsrUuid) {
                        modal.openModal({
                            content: "msg.ptl.00767", // This company representative has been already registered. Do you want to change it? !== 해당 업체의 대표자가 이미 등록되어 있습니다. 변경하시겠습니까? ==!
                            onConfirm: () => {
                                data.coRppnYn = "Y";
                            },
                            onCancel: () => {
                                data.coRppnYn = "N";
                            },
                            onClose: () => {
                                form.coEmpInfo.handleSubmit(
                                    () => {
                                        if (empStatus === "new") {
                                            fetch.createCoEmpUsr.fetch({ ...data, coTin: coTin });
                                        } else if (empStatus === "update") {
                                            fetch.updateCoEmpUsr.fetch({ ...data });
                                        }
                                    },
                                    () => {
                                        toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                                    },
                                )();
                            },
                        });
                    } else {
                        form.coEmpInfo.handleSubmit(
                            () => {
                                if (empStatus === "new") {
                                    fetch.createCoEmpUsr.fetch({ ...data, coTin: coTin });
                                } else if (empStatus === "update") {
                                    fetch.updateCoEmpUsr.fetch({ ...data });
                                }
                            },
                            () => {
                                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                            },
                        )();
                    }
                },
            });
        },
        // Delete Company Employee !== 업체 직원 삭제 ==!
        onDeleteEmpBtnClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    const data = form.coEmpInfo.getValues();
                    fetch.deleteEmp.fetch(data);
                },
            });
        },
        // Declarant Add Button Click!== 업체 CFA 그리드 추가 버튼 ==!
        onDcltAddBtnClick: () => {
            setDcltStatus("new");
            form.coDcltInfo.clearValues({});
            form.coDcltInfo.setValues({ useYn: "Y" });
        },
        // on Save Company Declarant Button Click Event Handler  !== 업체 신고인 등록 ==!
        onSaveDcltBtnClick: () => {
            modal.openModal({
                content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.coDcltInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        return;
                    }
                    form.coDcltInfo.handleSubmit(
                        () => {
                            const data = form.coDcltInfo.getValues();
                            if (dcltStatus === "new") {
                                fetch.createCoDclt.fetch({ ...data, coTin: form.usrAplyInfo.getValue("coTin") });
                            } else if (dcltStatus === "update") {
                                fetch.updateCoDclt.fetch({ ...data, coTin });
                            }
                        },
                        () => {
                            toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        },
                    )();
                },
            });
        },
        // Delete Company Declarant !== 업체 코드 삭제 ==!
        onDeleteDcltBtnClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    const data = form.coDcltInfo.getValues();
                    fetch.deleteCoDclt.fetch({ ...data, coTin });
                },
            });
        },

        checkUsrTin: () => {
            const xtrnUsrIdntfNo = form.coEmpInfo.getValue("xtrnUsrIdntfNo");
            if (xtrnUsrIdntfNo) {
                fetch.getCoDuplCheck.fetch(xtrnUsrIdntfNo);
            } else {
                form.coEmpInfo.clearErrors("xtrnUsrIdntfNo");
            }
        },

        // Initial Password Button Click !== 비밀번호 초기화 버튼 클릭 ==!
        onPwdBtnClick: (data: any) => {
            modal.openModal({
                content: "msg.ptl.00749", // Do you want to reset your password? !== 비밀번호를 초기화 하시겠습니까? ==!
                onConfirm: () => {
                    fetch.updateCoUsrPwdInit.fetch(data);
                },
            });
        },

        onUsrStatChange: (value: any) => {
            if (value == 2) {
                form.coEmpInfo.setSchema("mdfyRsn", { required: true });
            } else {
                form.coEmpInfo.setSchema("mdfyRsn", { required: false });
            }
        },

        onLcmWrhsCstmOfceCdChange: () => {
            if (isCstmOfceCdSelectableLcne()) {
                // EPZ 면허 보유 세관 수를 면허 수로 제한
                const epzLcnePrmssInfo = epzPrmss;
                if (!comnUtils.isUndefined(epzLcnePrmssInfo)) {
                    let epzLcneCnt = epzLcnePrmssInfo.epzLcneCnt; // EPZ 면허 수
                    let cstmOfceCdCnt = epzLcnePrmssInfo.cstmOfceCdCnt; // EPZ 면허 등록 세관 수
                    let cstmOfceCdSet = epzLcnePrmssInfo.cstmOfceCdSet; // EPZ 면허 등록 세관
                    if (epzLcneCnt > cstmOfceCdCnt) {
                        // EPZ 면허 수보다 등록된 세관 수가 적은 경우
                        return;
                    }
                    if (epzLcneCnt >= 1 && epzLcneCnt == cstmOfceCdCnt) {
                        // 면허 1개 이상 발급 && EPZ 면허 수와 등록된 세관 수가 동일한 경우
                        const cstmOfceCd = form.coTpAplyInfo.getValue("cstmOfceCd");
                        if (!comnUtils.isUndefined(cstmOfceCd) && !comnUtils.isEmptyString(cstmOfceCd)) {
                            // 기존에 등록된 세관을 포함하는 경우
                            if (cstmOfceCdSet.includes(cstmOfceCd)) {
                                return;
                            }
                        }
                    }
                }
                form.coTpAplyInfo.setValue("cstmOfceCd", currLcmWrhs.glblCstmOfceCd);
            } else {
                form.coTpAplyInfo.setValue("cstmOfceCd", currLcmWrhs.glblCstmOfceCd);
            }
        },

        onLcmWrhsCoCdSlctChange: () => {
            let bfrCoCdSelect = "";
            if (currLcmWrhs?.hasOwnProperty("coCd")) {
                bfrCoCdSelect = currLcmWrhs.coCd;
            }

            const coTpCd = form.coTpAplyInfo.getValue("coTpCd");
            const coCdSelect = form.coTpAplyInfo.getValue("coCdSelect");
            if (isLcmWrhsSelectTrgt()) {
                const currLcm = findLcmsWrhs(coTpCd, coCdSelect);
                if (!!currLcm) {
                    if (bfrCoCdSelect !== currLcm?.coCd) {
                        form.coTpAplyInfo.setValue("cstmOfceCd", currLcm.glblCstmOfceCd);
                        form.coTpAplyInfo.setValue("coCd", currLcm?.cocd);
                        setCurrLcmWrhs(currLcm);
                    }
                } else {
                    form.coTpAplyInfo.setValue("cstmOfceCd", "");
                    form.coTpAplyInfo.setValue("coCd", "");
                    setCurrLcmWrhs({});
                }
            } else {
                if (Object.keys(currLcmWrhs).length !== 0) {
                    form.coTpAplyInfo.setValue("cstmOfceCd", "");
                    form.coTpAplyInfo.setValue("coCd", "");
                }
                setCurrLcmWrhs({});
            }
        },

        showValidMsg: () => {
            const coTpAplyInfo = form.coTpAplyInfo.getValue();
            let dataNm = "";

            if (comnUtils.isEmptyString(coTpAplyInfo.cstmOfceCd) || comnUtils.isUndefined(coTpAplyInfo.cstmOfceCd)) {
                dataNm = t("L_CSTM_OFCE_CD"); // Customs Office Code !== 세관 사무소 코드 ==!
            }

            if (comnUtils.isEmptyString(coTpAplyInfo.coTpCd) || comnUtils.isUndefined(coTpAplyInfo.coTpCd)) {
                dataNm = t("L_CO_TP_CD"); // Company Type !== 업체 타입 ==!
            } else {
                if (CO_CD_LCNE_SET.has(coTpAplyInfo.coTpCd)) {
                    if (
                        comnUtils.isEmptyString(coTpAplyInfo.coCdSelect) ||
                        comnUtils.isUndefined(coTpAplyInfo.coCdSelect)
                    ) {
                        dataNm = t("L_CO_CD"); // Company Code !== 업체 코드 ==!
                        return true;
                    }
                } else {
                    if (CO_CD_INPUT_TRGT_SET.has(coTpAplyInfo.coTpCd)) {
                        if (comnUtils.isEmptyString(coTpAplyInfo.coCd) || comnUtils.isUndefined(coTpAplyInfo.coCd)) {
                            dataNm = t("L_CO_CD"); // Company Code !== 업체 코드 ==!
                        }
                    }
                }
            }

            toast.showToast({
                type: "warning",
                content: t("msg.cgm.00350", { 0: t("L_CO_CD") + " TAB", 1: dataNm }),
            }); // {0} [{1}] is mandatory. !== {0} [{1}] 은(는) 필수 입니다. ==!
        },
    };

    useEffect(() => {
        fetch.getCoInfoMgmt.fetch();

        grid.coEmpCoCdList.clearData();
        grid.coEmpCoCdList.setEdit("cell", "useYn", true);

        form.coEmpInfo.setEditable(false);
        form.coHistInfo.setEditable(false);
        form.coDcltInfo.setEditable(false);

        form.coTpAplyInfo.schema.coTpCd.tmpExcludes = form.coTpAplyInfo.schema.coTpCd.excludes;

        const store = getStore(PGEUIDS.ptli0905004q);
        store && tab.setActive(store.activeTab);
    }, []);

    useEffect(() => {
        form.coTpAplyInfo.setEditable(coCdStatus !== "select");

        if (coCdStatus !== "new") {
            form.coTpAplyInfo.setSchema("coTpCd", { excludes: form.coTpAplyInfo.schema.coTpCd.tmpExcludes });
            form.coTpAplyInfo.setEditable("bsopId", false);
        }
        setCurrLcmWrhs({});
    }, [coCdStatus]);

    useEffect(() => {
        form.coEmpInfo.setEditable(empStatus != null);
    }, [empStatus]);

    useEffect(() => {
        form.coDcltInfo.setEditable(dcltStatus != null);
        form.coDcltInfo.setEditable("dcltTin", dcltStatus === "new");
    }, [dcltStatus]);

    const coTpCd = form.coTpAplyInfo.watch("coTpCd");
    const coSn = form.coTpAplyInfo.watch("coSn");
    const coCdSelect = form.coTpAplyInfo.watch("coCdSelect");
    const cstmOfceCd = form.coTpAplyInfo.watch("cstmOfceCd");

    useEffect(() => {
        if (coCdStatus === "new") {
            form.coTpAplyInfo.setValue("cstmOfceCd", ""); // 초기화
        }
        form.coTpAplyInfo.setValue("coCd", "");
        form.coTpAplyInfo.setValue("coCdSelect", "");
    }, [coTpCd]);

    useEffect(() => {
        if (coCdStatus === "new" && !cstmOfceCd && cstmOfceCd !== "") {
            form.coTpAplyInfo.setValue("cstmOfceCd", ""); // 초기화
        }
    }, [coCdSelect]);

    const findLcmsWrhs = (coTpCd: string, coCd: string) => {
        const lcmWrhsCoTpMap = lcmWrhsCoTpMapRef.current;
        if (lcmWrhsCoTpMap?.hasOwnProperty(coTpCd)) {
            const currLcm = lcmWrhsCoTpMap[coTpCd].find((x: any) => x.coCd === coCd);
            return currLcm || null;
        }
        return null;
    };

    const isFieldVisible = (field: string) => {
        const visibleFields = FIELD_VISIBILITY_MAP.get(coTpCd) || new Set();
        return visibleFields.has(field);
    };

    const isAtchFileVisible = (field: string) => {
        const visibleFields = ATCH_FILE_VISIBILITY_MAP.get(coTpCd) || new Set();
        return visibleFields.has(field);
    };

    const isCoCdInputTrgt = () => {
        return CO_CD_INPUT_TRGT_SET.has(coTpCd);
    };
    const isCoCdInputTasacTrgt = () => CO_CD_INPUT_TASAC_SET.has(coTpCd);
    const isSaveCocd = () => {
        return typeof coSn !== "undefined";
    };
    const isCurrCdTz = () => {
        return form.usrAplyInfo.getValue("aplcCntyCd") === "TZ";
    };

    const isLcmWrhsSelectTrgt = (coSnParam: string = coSn, coTpCdParam: string = coTpCd) => {
        return (
            comnUtils.isEmpty(coSnParam) &&
            CO_CD_LCNE_SET.has(coTpCdParam) &&
            coTpCdParam !== "C" &&
            coTpCdParam !== "CS"
        );
    };

    // const isCstmOfceCdSelectableLcne = () => {
    //     return new Set(["WE", "WG", "WP"]).has(coTpCd); // EPZ LICENSE
    // };
    // coTpCd가 WE, WG, WP일 때, true 값을 출력 또는 glblCstmOfceCd가 null값이거나 빈 값이 들어올 때 true를 출력
    const isCstmOfceCdSelectableLcne = () => {
        const isEpzType = new Set(["WE", "WG", "WP"]).has(coTpCd);
        const isGlblCstmOfceCdNull = comnUtils.isEmpty(currLcmWrhs?.glblCstmOfceCd);
        // 이 조건을 만족할 때만 검색 비활성화 -> 그 외는 모두 검색 가능
        return isEpzType || isGlblCstmOfceCdNull;
    };

    const getLcmWrhsOptionArr = () => {
        let lcmWrhsCoTpOptions: any[] = [];
        const lcmWrhsCoTpMap = lcmWrhsCoTpMapRef.current;
        if (isLcmWrhsSelectTrgt() && coCdStatus === "new" && lcmWrhsCoTpMap?.hasOwnProperty(coTpCd)) {
            lcmWrhsCoTpOptions = lcmWrhsCoTpMap[coTpCd]?.map((item: any) => ({
                label: item.coCd,
                value: item.coCd,
                cstmOfceCd: item.glblCstmOfceCd,
                cstmOfceCdNm: item.glblCstmOfceNm,
            }));
        }
        return lcmWrhsCoTpOptions;
    };

    const isLcneUseTpCdSelectTrgt = () => {
        return "C" === coTpCd;
    };

    const isCellGroupVisible = FIELD_VISIBILITY_MAP.get(coTpCd)?.size;

    return (
        <Page>
            <Tab {...tab.tab}>
                <Tab.Panel>
                    <Group>
                        <Group.Header>
                            <Group.Title title={"T_CO_INFO"} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.coTin} edit={false} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.coNm} />
                                    <Group.Control {...form.usrAplyInfo.schema.inwdCoYn} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.rprsTelno} />
                                    <Group.Control {...form.usrAplyInfo.schema.rprsFaxNo} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.rprsEml} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.coAddr} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.tinStat} />
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                    <Group>
                        <Group.Header>
                            <Group.Title title={t("T_RPPN_INFO")} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.xtrnUsrIdntfNo} edit={false} />
                                    <Group.Control {...form.usrAplyInfo.schema.usrNm} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.aplcCntyCd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.frgnrYn} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.usrMoblNo} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.usrEmlAddr} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.usrAplyInfo.schema.rsdtAddr} />
                                </Group.Row>
                            </Group.Section>
                            <Layout direction="row">
                                <Layout.Right>
                                    <Button role="save" onClick={() => handler.onSaveBtnClick()} />
                                </Layout.Right>
                            </Layout>
                        </Group.Body>
                    </Group>
                </Tab.Panel>
                <Tab.Panel>
                    <Group>
                        {/* Company Type Part /  Customs Office Code 첫 클릭 시 , 출력이 되지 않음 << 수정 필요*/}
                        <Group.Header>
                            {/* cotpArea */}
                            <Group.Title title={t("T_CO_TP")} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Layout gap={4} direction="col">
                                    <Layout direction="row">
                                        <Layout.Right>
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => handler.onCoCdAddBtnClick()}
                                            >
                                                {t("B_ADD")}
                                            </Button>
                                        </Layout.Right>
                                    </Layout>
                                    <Grid {...grid.coTpList.grid} onRowClick={handler.onCoTpListRowClick} />
                                </Layout>
                            </Group.Section>
                            <Group.Section key={form.coTpAplyInfo.schema.key}>
                                <Group.Row>
                                    <Group.Control
                                        {...form.coTpAplyInfo.schema.coTpCd}
                                        edit={coCdStatus === "new"}
                                        onChange={(val: any) => {
                                            const selected = val?.target?.value ?? val;

                                            // C/CS만 제한
                                            if (selected === "C" || selected === "CS") {
                                                const list = grid.coTpList.getData?.() || [];
                                                const currentSn = form.coTpAplyInfo.getValue("coSn"); // 현재 편집 중 레코드 식별자

                                                // 현재 행(coSn)은 제외하고, 다른 행에 C/CS가 이미 있는지 검사
                                                const exists = list.some(
                                                    (item: any) =>
                                                        (item.coTpCd === "C" || item.coTpCd === "CS") &&
                                                        item.coSn !== currentSn,
                                                );

                                                if (exists) {
                                                    // 중복이면 선택 무효 + 안내
                                                    modal.openModal({ content: "msg.ptl.00831" });
                                                    // 내부 onChange가 먼저 적용됐을 수 있으니 다음 틱에서 강제 롤백
                                                    setTimeout(() => {
                                                        form.coTpAplyInfo.setValue("coTpCd", "");
                                                    }, 0);
                                                    return;
                                                }
                                            } else {
                                                // 정상 케이스는 반영
                                                form.coTpAplyInfo.setValue("coTpCd", selected);
                                            }
                                        }}
                                    />
                                    {!isLcmWrhsSelectTrgt() && (
                                        <Group.Control
                                            {...form.coTpAplyInfo.schema.cstmOfceCd}
                                            edit={coCdStatus === "new"}
                                        />
                                    )}

                                    {/* Customs Office Code Part*/}
                                    {isLcmWrhsSelectTrgt() && (
                                        <Group.Control
                                            {...form.coTpAplyInfo.schema.cstmOfceCd}
                                            edit={comnUtils.isEmpty(coSn)}
                                            disableSearch={!isCstmOfceCdSelectableLcne()}
                                            readOnlyInput={!isCstmOfceCdSelectableLcne()}
                                            borderColor={!isCstmOfceCdSelectableLcne() ? "info" : ""}
                                            onChange={() => handler.onLcmWrhsCstmOfceCdChange()}
                                        />
                                    )}
                                </Group.Row>
                                <Group.Row>
                                    {!isLcmWrhsSelectTrgt() && isCoCdInputTrgt() && (
                                        <Group.Control
                                            {...form.coTpAplyInfo.schema.coCd}
                                            maxLength={CO_CD_INPUT_PROPERTIES[coTpCd].maxLength}
                                            placeholder={CO_CD_INPUT_PROPERTIES[coTpCd].placeholder}
                                            edit={coCdStatus === "new"}
                                        />
                                    )}

                                    {!isLcmWrhsSelectTrgt() && !isCoCdInputTrgt() && (
                                        <Group.Control
                                            {...form.coTpAplyInfo.schema.coCdRo}
                                            edit={coCdStatus === "new"}
                                        />
                                    )}
                                    {isLcmWrhsSelectTrgt() && (
                                        <Group.Control
                                            {...form.coTpAplyInfo.schema.coCdSelect}
                                            edit={coCdStatus === "new"}
                                            options={getLcmWrhsOptionArr()}
                                            onChange={() => handler.onLcmWrhsCoCdSlctChange()}
                                        />
                                    )}
                                    <Group.Control {...form.coTpAplyInfo.schema.useYn} />
                                </Group.Row>
                                {/* 새롭게 추가된 내용. Company Code에서 Company Type Name 출력*/}
                                <Group.Row>
                                    <Group.Control {...form.coTpAplyInfo.schema.coTpNm} />
                                    {!isLcmWrhsSelectTrgt() &&
                                        isCoCdInputTrgt() &&
                                        isCoCdInputTasacTrgt() &&
                                        isSaveCocd() && (
                                            <Group.Control
                                                {...form.coTpAplyInfo.schema.tasacCrtfNoStr}
                                                edit={false}
                                            ></Group.Control>
                                        )}
                                    {!isLcmWrhsSelectTrgt() &&
                                        isCoCdInputTrgt() &&
                                        isCoCdInputTasacTrgt() &&
                                        !isSaveCocd() &&
                                        isCurrCdTz() && (
                                            <Group.Control
                                                {...form.coTpAplyInfo.schema.tasacCrtfNo}
                                                edit={comnUtils.isEmpty(coSn)}
                                                placeholder="TASAC License Number"
                                            ></Group.Control>
                                        )}
                                </Group.Row>
                                {isLcneUseTpCdSelectTrgt() && (
                                    <Group.Row>
                                        <Group.Control {...form.coTpAplyInfo.schema.lcneUseTpCd} />
                                    </Group.Row>
                                )}

                                {isCellGroupVisible != null && isCellGroupVisible > 0 && (
                                    <Group.Row>
                                        {isFieldVisible("gainCfrmOmsnYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.gainCfrmOmsnYn} />
                                        )}
                                        {isFieldVisible("gaotCfrmOmsnYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.gaotCfrmOmsnYn} />
                                        )}
                                        {isFieldVisible("stckCretCtraTpCd") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.stckCretCtraTpCd} />
                                        )}
                                        {isFieldVisible("stckClarCtraTpCd") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.stckClarCtraTpCd} />
                                        )}
                                    </Group.Row>
                                )}
                                {isCellGroupVisible != null && isCellGroupVisible > 0 && (
                                    <Group.Row>
                                        {isFieldVisible("autoCriYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.autoCriYn} />
                                        )}
                                        {isFieldVisible("autoCroYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.autoCroYn} />
                                        )}
                                        {isFieldVisible("wrhsDpstDays") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.wrhsDpstDays} />
                                        )}
                                        {isFieldVisible("psnWrhsYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.psnWrhsYn} />
                                        )}
                                        {isFieldVisible("spcdlWrhsYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.spcdlWrhsYn} />
                                        )}
                                        {isFieldVisible("rskCagYn") && (
                                            <Group.Control {...form.coTpAplyInfo.schema.rskCagYn} />
                                        )}
                                    </Group.Row>
                                )}
                                {isAtchFileVisible("bsopId") && (
                                    <Group.Row>
                                        <Group.Control {...form.coTpAplyInfo.schema.bsopId} controlSize={10} />
                                    </Group.Row>
                                )}
                            </Group.Section>
                            <Layout direction="row">
                                <Layout.Right>
                                    {coCdStatus != null && (
                                        <Button role="save" onClick={() => handler.onSaveCoCdBtnClick()} />
                                    )}
                                    {coCdStatus === "update" && (
                                        <Button role="delete" onClick={() => handler.onDeleteCoCdBtnClick()} />
                                    )}
                                </Layout.Right>
                            </Layout>
                        </Group.Body>
                    </Group>
                </Tab.Panel>
                <Tab.Panel>
                    <Group>
                        <Group.Header>
                            <Group.Title title={t("T_CO_EMP_LIST")} />
                        </Group.Header>
                        <Group.Body>
                            <Group.Section>
                                <Layout gap={4} direction="col">
                                    <Layout direction="row">
                                        <Layout.Right>
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => handler.onEmpAddBtnClick()}
                                            >
                                                {t("B_ADD")}
                                            </Button>
                                        </Layout.Right>
                                    </Layout>
                                    <Grid
                                        {...grid.coEmpList.grid}
                                        onRowClick={handler.onCoEmpListRowClick}
                                        render={render.coEmpList}
                                    />
                                </Layout>
                            </Group.Section>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control
                                        {...form.coEmpInfo.schema.xtrnUsrIdntfNo}
                                        rightButton={{ icon: "search", onClick: () => fetch.getTinNoLstForUsr.fetch() }}
                                        edit={empStatus === "new"}
                                    />
                                    <Group.Control
                                        {...form.coEmpInfo.schema.usrStatCd}
                                        edit={empStatus === "update"}
                                        onChange={handler.onUsrStatChange}
                                    />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.usrNm} />
                                    <Group.Control {...form.coEmpInfo.schema.frgnrYn} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.aplcCntyCd} />
                                    <Group.Control {...form.coEmpInfo.schema.usrMoblNo} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.rsdtAddr} />
                                    <Group.Control {...form.coEmpInfo.schema.usrEmlAddr} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.coRppnYn} />
                                    <Group.Control {...form.coEmpInfo.schema.cstmOfceCd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.mdfyRsn} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coEmpInfo.schema.mdfyRsnFile} />
                                </Group.Row>
                            </Group.Section>

                            {form.coEmpInfo.getValue("coRppnYn") != "Y" &&
                                ["update", "new"].includes(empStatus || "") && (
                                    <Group.Section>
                                        <Group.Title title={"T_EMP_CO_CD_LIST"} />
                                        <Grid
                                            {...grid.coEmpCoCdList.grid}
                                            data={fetch.getEmpUsrCoCdList.data?.empUsrCoCdList}
                                        />
                                    </Group.Section>
                                )}

                            <Layout direction="row">
                                <Layout.Right>
                                    {empStatus != null && (
                                        <Button role="save" onClick={() => handler.onSaveEmpBtnClick()} />
                                    )}
                                    {empStatus === "update" && (
                                        <Button role="delete" onClick={() => handler.onDeleteEmpBtnClick()} />
                                    )}
                                </Layout.Right>
                            </Layout>
                        </Group.Body>
                    </Group>
                </Tab.Panel>
                <Tab.Panel>
                    <Group>
                        <Group.Body>
                            <Layout>
                                <Layout.Left>
                                    <Group.Title title={"T_CFA_LIST"} titleSize={1}></Group.Title>
                                </Layout.Left>
                            </Layout>
                            <Group.Section>
                                <Layout gap={4} direction="col">
                                    <Layout direction="row">
                                        <Layout.Right>
                                            <Button
                                                variant="outline-primary"
                                                onClick={() => handler.onDcltAddBtnClick()}
                                            >
                                                {t("B_ADD")}
                                            </Button>
                                        </Layout.Right>
                                    </Layout>
                                    <Grid
                                        {...grid.coDcltList.grid}
                                        data={fetch.getCoDcltList.data?.coDcltList}
                                        onRowClick={handler.onCoDcltListRowClick}
                                    />
                                </Layout>
                            </Group.Section>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control
                                        {...form.coDcltInfo.schema.dcltTin}
                                        rightButton={{ icon: "search", onClick: () => handler.getCoDcltDtl() }}
                                    />
                                    <Group.Control {...form.coDcltInfo.schema.dcltNm} edit={false} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coDcltInfo.schema.dcltAddr} edit={false} />
                                    <Group.Control {...form.coDcltInfo.schema.dcltTelno} edit={false} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.coDcltInfo.schema.dcltFaxNo} edit={false} />
                                    <Group.Control {...form.coDcltInfo.schema.useYn} />
                                </Group.Row>
                            </Group.Section>
                            <Layout direction="row">
                                <Layout.Right>
                                    {dcltStatus != null && (
                                        <Button role="save" onClick={() => handler.onSaveDcltBtnClick()} />
                                    )}
                                    {dcltStatus === "update" && (
                                        <Button role="delete" onClick={() => handler.onDeleteDcltBtnClick()} />
                                    )}
                                </Layout.Right>
                            </Layout>
                        </Group.Body>
                    </Group>
                </Tab.Panel>
            </Tab>
            <Layout direction="row">
                <Layout.Left>
                    <Button role="list" onClick={() => navigate(URLS.ptli0905001q)} />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
